import { Link } from "react-router-dom";
import { Check, Circle, X, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { buttonVariants } from "@/components/ui/button";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { InfoBanner } from "@/components/ui/banner";
import { IdentifierValue } from "@/components/ui/identifier-value";
import { cn } from "@/lib/utils";
import { formatAgo } from "@/lib/format-ago";
import { usePermission } from "@/hooks/use-permission";
import { formatTransferAmount } from "@/features/transfers/transfer-amount";
import { INITIATOR_KIND_LABEL } from "@/features/transfers/transfer-table";
import { buildLifecycle, type LifecycleStage } from "@/features/transfers/transfer-lifecycle";
import { transferStatusLabel } from "@/features/transfers/transfer-state";
import { useMobileSigningLocale } from "@/i18n/use-mobile-signing-locale";
import type { TransferDetail, TransferRecord } from "@/types";

/**
 * Transfer detail (ALL-5794; flows OPS-03, TRF-102; page spec transfers-list action
 * operator_views_transfer_detail). Founder, 2026-10-02: the transfers page "needs to be redesigned
 * and filled".
 *
 * Everything on this page is a field of the gateway's TransferView (GET /v1/transfers/{id}):
 * status, source wallet, destination, amount, asset, network, timeline, failure, tx hash,
 * created/updated. The stage tracker is built from status + timeline (transfer-lifecycle.ts).
 * There are no buttons that only raise a toast: the page offers navigation to the approvals queue
 * (for people who can approve) and to a new transfer, both handled by their own pages, which the
 * server authorises.
 */

const EXPLORERS: Record<string, string> = {
  sepolia: "https://sepolia.etherscan.io",
  "ethereum-sepolia": "https://sepolia.etherscan.io",
  hoodi: "https://hoodi.etherscan.io",
  "ethereum-hoodi": "https://hoodi.etherscan.io",
  mainnet: "https://etherscan.io",
  ethereum: "https://etherscan.io",
};

/** Block-explorer link for a tx hash, only for networks we know the explorer of. */
export function explorerTxUrl(network: string, txHash: string): string | null {
  const base = EXPLORERS[network.trim().toLowerCase()];
  return base && /^0x[0-9a-fA-F]{64}$/.test(txHash) ? `${base}/tx/${txHash}` : null;
}

function exactTime(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? new Date(ms).toLocaleString() : undefined;
}

function TimeValue({ iso }: { iso?: string }) {
  const exact = exactTime(iso);
  if (!exact) return <>—</>;
  const ago = formatAgo(iso);
  return (
    <span title={iso}>
      {exact}
      {ago && <span className="ml-1.5 text-muted">({ago} ago)</span>}
    </span>
  );
}

/** ALL-5802: a detail value the gateway did not project — muted "unknown", never blank or a guess. */
function UnknownText() {
  return <span className="text-muted">unknown</span>;
}

function KVPanel({ rows }: { rows: { k: string; v: React.ReactNode }[] }) {
  return (
    <dl className="overflow-hidden rounded-[10px] border border-line-soft bg-surface">
      {rows.map((row, i) => (
        <div
          key={`${row.k}-${i}`}
          className={cn("flex flex-wrap items-start justify-between gap-x-4 gap-y-1 px-5 py-3", i < rows.length - 1 && "border-b border-line-soft")}
        >
          <dt className="shrink-0 pt-0.5 text-[12px] text-muted">{row.k}</dt>
          <dd className="min-w-0 max-w-full break-words text-right text-[12.5px] font-medium text-ink sm:max-w-[65%]">{row.v}</dd>
        </div>
      ))}
    </dl>
  );
}

const STAGE_ICON: Record<LifecycleStage["status"], React.ReactNode> = {
  done: <Check aria-hidden className="size-3.5" />,
  current: <Circle aria-hidden className="size-2.5 fill-current" />,
  failed: <X aria-hidden className="size-3.5" />,
  unknown: <AlertTriangle aria-hidden className="size-3.5" />,
  upcoming: null,
};

const STAGE_STYLE: Record<LifecycleStage["status"], string> = {
  done: "border-success bg-success text-white",
  current: "border-primary bg-primary-soft text-primary",
  failed: "border-danger bg-danger text-white",
  unknown: "border-warning bg-warning-soft text-warning",
  upcoming: "border-line bg-surface text-muted",
};

const STAGE_WORD: Record<LifecycleStage["status"], string> = {
  done: "done",
  current: "in progress",
  failed: "stopped here",
  unknown: "outcome unknown",
  upcoming: "not reached",
};

function LifecycleTracker({ stages }: { stages: LifecycleStage[] }) {
  return (
    <ol className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:grid-cols-6" aria-label="Transfer progress">
      {stages.map((s) => (
        <li
          key={s.id}
          data-stage={s.id}
          data-status={s.status}
          aria-current={s.status === "current" ? "step" : undefined}
          className="flex items-start gap-2.5 rounded-lg border border-line bg-surface px-3 py-2.5"
        >
          <span className={cn("mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border", STAGE_STYLE[s.status])}>
            {STAGE_ICON[s.status]}
          </span>
          <span className="min-w-0">
            <span className={cn("block text-[12.5px] font-semibold", s.status === "upcoming" ? "text-muted" : "text-ink")}>{s.label}</span>
            <span className="block text-[11px] text-muted">
              <span className="sr-only">{STAGE_WORD[s.status]}. </span>
              {s.at ? <span title={s.at}>{exactTime(s.at)}</span> : s.status === "upcoming" ? "" : <span aria-hidden>{STAGE_WORD[s.status]}</span>}
            </span>
          </span>
        </li>
      ))}
    </ol>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-6">
      <h2 className="mb-3 text-[15px] font-bold text-ink">{title}</h2>
      {children}
    </section>
  );
}

export default function TransferDetailView({
  detail,
  sourceWalletName,
}: {
  detail: TransferDetail;
  /** Readable source wallet name from the wallets list (ALL-5795), when it resolved. */
  sourceWalletName?: string;
}) {
  const record = detail.record as TransferRecord | undefined;
  const canApprove = usePermission("approval:approve");
  const canCreate = usePermission("transfer:create");
  const mobileSigning = useMobileSigningLocale();

  if (!record) return null;
  const t = record.transfer;
  const state = t.state;
  const stages = buildLifecycle(t.status, record.timeline, t.created);
  const from = sourceWalletName || t.sourceName || t.wallet || "—";
  const explorer = t.txHash ? explorerTxUrl(t.network ?? "", t.txHash) : null;
  const amountKnown = t.amount !== "" && t.amount !== undefined;
  // ALL-5802: the projection's nullable initiator/provider/approval, read as-is. A missing one is
  // an unknown the rows render as a muted word, never a fabricated name or count.
  const initiatorName = t.initiator?.displayName || t.initiator?.principalId || "";
  const initiatorKind = t.initiator?.kind;
  const providerName = t.provider?.displayName || "";
  const approvalRequired = t.approval?.required;
  const approvalApproved = t.approval?.approved;
  const approvalKnown = approvalRequired != null && approvalApproved != null;

  const rows: { k: string; v: React.ReactNode }[] = [
    { k: "Intent ID", v: <IdentifierValue value={t.id} kind="intent" label={false} /> },
    {
      k: "Status",
      v: <Badge variant={t.statusVariant} dot={false} size="sm">{t.statusLabel}</Badge>,
    },
    {
      k: "Amount",
      v: amountKnown ? (
        <span className="font-mono tabular-nums">
          {formatTransferAmount(t.amount)} <span className="font-sans text-muted">{t.asset}</span>
        </span>
      ) : "—",
    },
    { k: "Network", v: <span className="capitalize">{t.network || "—"}</span> },
    {
      k: "From",
      v: t.wallet ? (
        <Link to={`/wallets/${encodeURIComponent(t.wallet)}`} className="font-semibold text-primary hover:underline">{from}</Link>
      ) : from,
    },
    { k: "To", v: <IdentifierValue value={t.destination} kind="address" label={false} /> },
    {
      k: "Initiator",
      v: initiatorName ? (
        <span>
          {initiatorName}
          <span className="ml-1.5 text-muted">{initiatorKind ? INITIATOR_KIND_LABEL[initiatorKind] : "unknown"}</span>
        </span>
      ) : (
        <UnknownText />
      ),
    },
    { k: "Provider", v: providerName || <UnknownText /> },
    {
      k: "Approval",
      v: approvalKnown ? (
        <span className="tabular-nums">
          {approvalApproved}/{approvalRequired}
        </span>
      ) : (
        <UnknownText />
      ),
    },
    { k: "Created", v: <TimeValue iso={t.created} /> },
    { k: "Last updated", v: <TimeValue iso={t.updated} /> },
    {
      k: "Transaction hash",
      v: t.txHash ? (
        <span className="inline-flex flex-wrap items-center justify-end gap-2">
          <IdentifierValue value={t.txHash} kind="tx" label={false} />
          {explorer && (
            <a
              href={explorer}
              target="_blank"
              rel="noreferrer"
              aria-label={`View transaction ${t.txHash} on the block explorer`}
              className="font-semibold text-primary underline"
            >
              Explorer ↗
            </a>
          )}
        </span>
      ) : (
        <span className="text-muted">Not broadcast yet</span>
      ),
    },
  ];

  return (
    <div className="max-w-[880px]">
      <Breadcrumb items={[{ label: "Transfers", href: "/transfers" }, { label: t.id }]} />

      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted">Transfer</div>
          <h1 className="mt-1 text-2xl font-bold text-ink" data-testid="transfer-headline">
            {amountKnown ? (
              <>
                <span className="font-mono tabular-nums">{formatTransferAmount(t.amount)}</span>{" "}
                <span className="text-muted">{t.asset}</span>
              </>
            ) : "Amount unknown"}
          </h1>
          <p className="mt-1 text-sm text-muted">
            From <span className="font-medium text-text">{from}</span>
            {t.network ? <> on <span className="capitalize">{t.network}</span></> : null}
          </p>
        </div>
        <Badge variant={t.statusVariant} dot={false}>{t.statusLabel}</Badge>
      </header>

      {state === "failed" && (
        <InfoBanner variant="danger" className="mb-4" title="This transfer failed">
          {record.failureReason || "The gateway recorded a failure without a reason."}
          {record.failureService && <span className="mt-1 block text-xs">Failed in: {record.failureService}</span>}
        </InfoBanner>
      )}
      {state === "rejected" && (
        <InfoBanner variant="danger" className="mb-4" title="This transfer was rejected">
          {record.failureReason || "It was rejected before signing. No funds moved."}
        </InfoBanner>
      )}
      {state === "outcome-unknown" && (
        <InfoBanner variant="warning" className="mb-4" title="Outcome unknown">
          The broadcast result is not confirmed yet. Do not send this transfer again until its on-chain state is known.
        </InfoBanner>
      )}
      {state === "pending-device-signature" && (
        <InfoBanner variant="info" className="mb-4" title={mobileSigning.transferState.pendingDeviceSignature}>
          <span dir={mobileSigning.dir}>{mobileSigning.transferState.pendingDeviceSignatureDetail}</span>
        </InfoBanner>
      )}
      {state === "pending-approval" && (
        <InfoBanner variant="info" className="mb-4" title="Waiting for approval">
          This transfer needs approval before it can be signed.
          {canApprove && (
            <Link to="/approvals" className="ml-1.5 font-semibold text-primary underline">Open approvals</Link>
          )}
        </InfoBanner>
      )}
      {record.degraded && (
        <InfoBanner variant="warning" className="mb-4" title="Some details may be out of date">
          Part of this transfer's status could not be read just now. Refresh to try again.
        </InfoBanner>
      )}

      <Section title="Progress">
        <LifecycleTracker stages={stages} />
      </Section>

      <Section title="Details">
        <KVPanel rows={rows} />
      </Section>

      <Section title="Activity">
        {record.timeline.length > 0 ? (
          <ol className="overflow-hidden rounded-lg border border-line bg-surface" data-testid="transfer-activity">
            {record.timeline.map((e, i) => (
              <li key={`${e.state}-${i}`} className={cn("flex flex-wrap items-baseline justify-between gap-2 px-5 py-3", i < record.timeline.length - 1 && "border-b border-line")}>
                <span className="text-[13px] font-semibold text-ink">{transferStatusLabel(e.state)}</span>
                <span className="text-[11.5px] text-muted" title={e.at || undefined}>{exactTime(e.at) ?? "time not recorded"}</span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="rounded-lg border border-dashed border-line px-5 py-4 text-sm text-muted">
            No status changes recorded yet.
          </p>
        )}
      </Section>

      <div className="mt-7 flex flex-wrap gap-2">
        <Link to="/transfers" className={buttonVariants({ variant: "secondary", size: "sm" })}>
          ← Back to Transfers
        </Link>
        {canCreate && (state === "failed" || state === "rejected") && (
          <Link to="/transfers/new" className={buttonVariants({ variant: "secondary", size: "sm" })}>
            New transfer
          </Link>
        )}
      </div>
    </div>
  );
}
